import { useEffect, useRef, useState } from "react";
import { motion, useMotionValue, useSpring, useTransform, useMotionTemplate } from "framer-motion";
import { ArrowUpRight, Play, CheckCircle2, CornerDownLeft } from "lucide-react";

const LINES = [
  <>Software is no longer</>,
  <>
    coded. It is <span className="text-[#2563eb]">spoken</span>
  </>,
  <>into existence.</>,
];

const GENERIC_STEPS = [
  "parsing intent → drafting product spec & personas",
  "schema: collections, relations & indexes inferred",
  "frontend: React 19 screens + state machines",
  "backend: FastAPI endpoints + JWT auth flows",
  "provisioning live URL → your-app.emergent.site",
];

const PRESETS = [
  {
    id: 1,
    label: "Uber for Dog Walkers",
    prompt: "Build an Uber-style dog walking marketplace with live map, booking, and Stripe payouts.",
    steps: [
      "parsing intent → 3 roles detected (owner, walker, admin)",
      "schema: users, dogs, bookings, payouts → MongoDB",
      "frontend: live map view, booking flow, walker dashboard",
      "backend: 24 endpoints, JWT auth, Stripe webhooks",
      "provisioning live URL → dogwalk.emergent.site",
    ],
    chips: ["React 19", "FastAPI", "MongoDB", "Stripe", "Live Map"],
  },
  {
    id: 2,
    label: "AI Legal Doc Analyzer",
    prompt: "Create an AI legal document analyzer with secure auth and clause extraction.",
    steps: [
      "parsing intent → 2 roles detected (lawyer, client)",
      "schema: documents, clauses, reviews → MongoDB",
      "frontend: upload flow, clause viewer, risk heatmap",
      "backend: LLM extraction pipeline, SOC2-grade auth",
      "provisioning live URL → lexbrief.emergent.site",
    ],
    chips: ["React 19", "FastAPI", "LLM Pipeline", "Auth", "PDF"],
  },
  {
    id: 3,
    label: "B2B CRM, Real-Time",
    prompt: "Spin up a B2B CRM with real-time WebSocket deal rooms and analytics.",
    steps: [
      "parsing intent → pipeline stages & deal rooms mapped",
      "schema: accounts, deals, activities → MongoDB",
      "frontend: kanban pipeline, live deal room, analytics",
      "backend: WebSocket sync layer, role-based access",
      "provisioning live URL → dealdesk.emergent.site",
    ],
    chips: ["React 19", "WebSockets", "MongoDB", "RBAC", "Charts"],
  },
];

const lineVariants = {
  hidden: { y: "115%" },
  visible: (i) => ({
    y: "0%",
    transition: { delay: 0.2 + i * 0.16, duration: 1, ease: [0.16, 1, 0.3, 1] },
  }),
};

export const Hero = () => {
  const sectionRef = useRef(null);
  const runRef = useRef(0);
  const [activeId, setActiveId] = useState(null);
  const [typed, setTyped] = useState("");
  const [stepsShown, setStepsShown] = useState(0);
  const [done, setDone] = useState(false);
  const [custom, setCustom] = useState("");
  const [activeSteps, setActiveSteps] = useState([]);
  const [chips, setChips] = useState([]);

  const mx = useMotionValue(0.5);
  const my = useMotionValue(0.5);
  const rotateX = useSpring(useTransform(my, [0, 1], [5, -5]), { stiffness: 120, damping: 20 });
  const rotateY = useSpring(useTransform(mx, [0, 1], [-5, 5]), { stiffness: 120, damping: 20 });
  const glowX = useTransform(mx, (v) => v * 100);
  const glowY = useTransform(my, (v) => v * 100);
  const glow = useMotionTemplate`radial-gradient(640px circle at ${glowX}% ${glowY}%, rgba(37,99,235,0.10), transparent 65%)`;

  const onMove = (e) => {
    const r = sectionRef.current?.getBoundingClientRect();
    if (!r) return;
    mx.set((e.clientX - r.left) / r.width);
    my.set((e.clientY - r.top) / r.height);
  };

  const runBuild = (id, prompt, steps, chipList) => {
    const runId = ++runRef.current;
    setActiveId(id);
    setTyped("");
    setStepsShown(0);
    setDone(false);
    setActiveSteps(steps);
    setChips(chipList);
    let i = 0;
    const typeIv = setInterval(() => {
      if (runRef.current !== runId) return clearInterval(typeIv);
      i += 3;
      setTyped(prompt.slice(0, i));
      if (i >= prompt.length) {
        clearInterval(typeIv);
        let s = 0;
        const stepIv = setInterval(() => {
          if (runRef.current !== runId) return clearInterval(stepIv);
          s += 1;
          setStepsShown(s);
          if (s >= steps.length) {
            clearInterval(stepIv);
            setDone(true);
          }
        }, 430);
      }
    }, 20);
  };

  useEffect(() => {
    const t = setTimeout(() => runBuild(PRESETS[0].id, PRESETS[0].prompt, PRESETS[0].steps, PRESETS[0].chips), 1600);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const submitCustom = (e) => {
    e.preventDefault();
    const text = custom.trim() || "Build my next product idea.";
    runBuild(0, text, GENERIC_STEPS, ["React 19", "FastAPI", "MongoDB", "Auth", "Deploy"]);
  };

  return (
    <section ref={sectionRef} onMouseMove={onMove} className="relative overflow-hidden pb-24 pt-32 sm:pt-40">
      <div className="hero-grid absolute inset-0" />
      <motion.div className="pointer-events-none absolute inset-0" style={{ background: glow }} />

      <div className="relative mx-auto grid max-w-7xl items-center gap-14 px-6 lg:grid-cols-12">
        <div className="lg:col-span-7">
          <motion.p
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.1, duration: 0.8 }}
            className="mb-8 inline-flex items-center gap-2 rounded-full border border-[#e8e8e4] bg-white px-4 py-1.5 font-mono text-[11px] uppercase tracking-[0.2em] text-slate-600 shadow-sm"
          >
            <span className="h-1.5 w-1.5 rounded-full bg-[#10b981]" /> Natural Language Compiler for Production Code
          </motion.p>

          <h1
            data-testid="hero-headline"
            className="font-display text-4xl font-bold leading-[1.05] text-[#0b0f1a] sm:text-5xl lg:text-6xl"
          >
            {LINES.map((line, i) => (
              <span key={i} className="block overflow-hidden pb-1">
                <motion.span className="block" custom={i} variants={lineVariants} initial="hidden" animate="visible">
                  {line}
                </motion.span>
              </span>
            ))}
          </h1>

          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.9, duration: 0.8, ease: [0.16, 1, 0.3, 1] }}
            className="mt-8 max-w-xl text-base leading-relaxed text-slate-500 sm:text-lg"
          >
            Describe complex web &amp; native mobile applications in plain human language. Emergent provisions the
            React frontend, FastAPI backend, MongoDB cluster, auth flows, and one-click cloud deployment.
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 1.05, duration: 0.8, ease: [0.16, 1, 0.3, 1] }}
            className="mt-10 flex flex-wrap items-center gap-4"
          >
            <a
              data-testid="hero-primary-cta"
              href="https://emergent.sh"
              target="_blank"
              rel="noopener noreferrer"
              className="btn-primary group inline-flex items-center gap-2.5 rounded-full px-7 py-3.5 text-sm font-semibold"
            >
              Launch Emergent Studio
              <ArrowUpRight className="h-4 w-4 transition-transform duration-300 group-hover:-translate-y-0.5 group-hover:translate-x-0.5" />
            </a>
            <a
              data-testid="hero-secondary-demo-cta"
              href="#live-engine"
              className="group inline-flex items-center gap-2.5 rounded-full border border-[#e8e8e4] bg-white px-7 py-3.5 text-sm font-semibold text-[#0b0f1a] shadow-sm transition-all duration-300 hover:-translate-y-0.5 hover:border-[#2563eb]/40 hover:text-[#2563eb]"
            >
              <Play className="h-4 w-4" />
              Watch it build live
            </a>
          </motion.div>

          <motion.p
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 1.25, duration: 0.8 }}
            className="mt-10 font-mono text-xs uppercase tracking-[0.2em] text-slate-400"
          >
            Trusted by 14,200+ founders, PMs &amp; indie hackers
          </motion.p>
        </div>

        <motion.div
          initial={{ opacity: 0, y: 40 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.7, duration: 1, ease: [0.16, 1, 0.3, 1] }}
          className="lg:col-span-5"
          style={{ perspective: 1200 }}
        >
          <motion.div
            style={{ rotateX, rotateY, transformStyle: "preserve-3d" }}
            className="console-shadow relative rounded-3xl bg-[#0b1120]"
          >
            <div className="flex items-center justify-between border-b border-white/10 px-5 py-3.5">
              <div className="flex items-center gap-1.5">
                <span className="h-2.5 w-2.5 rounded-full bg-[#ff5f57]" />
                <span className="h-2.5 w-2.5 rounded-full bg-[#febc2e]" />
                <span className="h-2.5 w-2.5 rounded-full bg-[#28c840]" />
              </div>
              <span className="font-mono text-[10px] uppercase tracking-[0.2em] text-slate-500">
                emergent — studio.build
              </span>
            </div>

            <div className="min-h-[300px] px-5 py-5 font-mono text-sm">
              <p className="leading-relaxed text-slate-200">
                <span className="text-[#60a5fa]">&gt; </span>
                {typed}
                {!done && <span className="caret ml-0.5 inline-block h-4 w-2 translate-y-0.5 bg-[#60a5fa]" />}
              </p>
              <div className="mt-5 space-y-2.5">
                {activeSteps.slice(0, stepsShown).map((s, i) => (
                  <motion.p
                    key={`${activeId}-${i}`}
                    initial={{ opacity: 0, x: -10 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ duration: 0.4 }}
                    className="flex items-start gap-2.5 text-xs leading-relaxed text-slate-400"
                  >
                    <CheckCircle2 className="mt-0.5 h-3.5 w-3.5 shrink-0 text-[#34d399]" />
                    {s}
                  </motion.p>
                ))}
              </div>
              {done && (
                <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} className="mt-5">
                  <div className="flex flex-wrap gap-2">
                    {chips.map((c) => (
                      <span
                        key={c}
                        className="rounded-full border border-[#60a5fa]/40 bg-[#2563eb]/20 px-3 py-1 text-[10px] uppercase tracking-[0.15em] text-[#93c5fd]"
                      >
                        {c}
                      </span>
                    ))}
                  </div>
                  <p className="mt-4 text-[10px] uppercase tracking-[0.2em] text-slate-500">
                    Build complete · avg 04:12 · exportable Git repo
                  </p>
                </motion.div>
              )}
            </div>

            <form onSubmit={submitCustom} className="flex items-center gap-2 border-t border-white/10 px-4 py-3">
              <input
                data-testid="hero-prompt-input"
                value={custom}
                onChange={(e) => setCustom(e.target.value)}
                placeholder="Describe your own app idea…"
                className="w-full bg-transparent px-1 py-1.5 font-mono text-xs text-slate-200 placeholder:text-slate-500 focus:outline-none"
              />
              <button
                data-testid="hero-prompt-submit-button"
                type="submit"
                className="flex items-center gap-1.5 rounded-full bg-[#2563eb] px-3.5 py-1.5 font-mono text-[10px] uppercase tracking-[0.15em] text-white transition-colors hover:bg-[#1d4ed8]"
              >
                Build
                <CornerDownLeft className="h-3 w-3" />
              </button>
            </form>
          </motion.div>

          <div className="mt-5 flex flex-wrap gap-2.5">
            {PRESETS.map((p) => (
              <button
                key={p.id}
                data-testid={`prompt-preset-button-${p.id}`}
                onClick={() => runBuild(p.id, p.prompt, p.steps, p.chips)}
                className={`rounded-full border px-4 py-2 font-mono text-[11px] uppercase tracking-[0.12em] shadow-sm transition-colors ${
                  activeId === p.id
                    ? "border-[#2563eb] bg-[#eff4ff] text-[#2563eb]"
                    : "border-[#e8e8e4] bg-white text-slate-500 hover:border-[#2563eb]/50 hover:text-[#0b0f1a]"
                }`}
              >
                {p.label}
              </button>
            ))}
          </div>
        </motion.div>
      </div>
    </section>
  );
};
